import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import toast from "react-hot-toast";
import { useTenantHeader } from "./TenantLayout";
import { RequestModal, normalizeRequest, PRIORITY_DOT } from "../components/RequestModal";
import { applyBillingEnumText, applyEnumText } from "../../utils/enums";
import {
  tenantFetch,
  getTenantId,
  isPrimaryOccupant,
  getTenantSession,
  API_BASE,
  parseArrayData,
  getDependentsWithAccess,
  getAssignedBuildings,
  getPaymentHistory,
  getCars,
  createDependentWithAccessCode,
  getMaintenanceCategories,
  submitMaintenanceRequest,
} from "./TenantAuth";

// Tenant dashboard preview. Built on the dashboard6 design system and fed by live tenant APIs.

function capitalizeFirst(str) {
  const t = String(str || "").trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning 🌤️";
  if (h < 17) return "Good Afternoon ☀️";
  return "Good Evening 🌑";
}

function parseISODate(iso) {
  if (!iso) return new Date();
  const [y, m, d] = String(iso).split("T")[0].split("-").map(Number);
  return new Date(y, m - 1, d);
}

function daysBetween(a, b) {
  const ms = 24 * 60 * 60 * 1000;
  const a0 = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  const b0 = new Date(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.floor((b0 - a0) / ms);
}

const HOUSE_TYPE_IMAGES = {
  "1 Bedroom Bungalow": "/assets/media/zangon/1bedroombungalo.jpg",
  "1 Bedrooms Bungalow": "/assets/media/zangon/1bedroombungalo.jpg",
  "2 Bedrooms Bungalow": "/assets/media/zangon/2bedroom.jpg",
  "3 Bedrooms Bungalow": "/assets/media/zangon/3bedroomsbungalo.jpg",
  "4 Bedrooms Duplex": "/assets/media/zangon/4bd.jpg",
  "5 Bedrooms Terrace": "/assets/media/zangon/5-light.jpg",
  "3 Bedroom Bungalow": "/assets/media/zangon/3bedroomsbungalo.jpg",
  "4 Bedroom Bungalow": "/assets/media/zangon/4bedroombungalo.jpg",
  "4 Bedrooms Bungalow": "/assets/media/zangon/4bedroombungalo.jpg",
  "6 Bedroom Bungalow": "/assets/media/zangon/6bedroombungalow.jpg",
  "6 Bedrooms Bungalow": "/assets/media/zangon/6bedroombungalow.jpg",
  "6 Bedroom Terrace Duplex": "/assets/media/zangon/6bedroomterraceduple.jpg",
  "6 Bedrooms Terrace Duplex": "/assets/media/zangon/6bedroomterraceduple.jpg",
};

function getHouseImage(typeName) {
  if (!typeName) return "/assets/media/zangon/3bedroomsbungalo.jpg";
  const norm = typeName.toLowerCase().replace(/[^a-z0-9]/g, "").replace(/bedrooms?/g, "bedroom");
  if (norm.includes("1bedroom") || norm.includes("1bd")) return "/assets/media/zangon/1bedroombungalo.jpg";
  if (norm.includes("2bedroom") || norm.includes("2bd")) return "/assets/media/zangon/2bedroom.jpg";
  if (norm.includes("3bedroombungalow") || norm.includes("3bedroombungalo") || norm.includes("3bd")) return "/assets/media/zangon/3bedroomsbungalo.jpg";
  if (norm.includes("4bedroombungalow") || norm.includes("4bedroombungalo")) return "/assets/media/zangon/4bedroombungalo.jpg";
  if (norm.includes("5bedroom")) return "/assets/media/zangon/5-light.jpg";
  if (norm.includes("6bedroombungalow")) return "/assets/media/zangon/6bedroombungalow.jpg";
  if (norm.includes("6bedroomterraceduplex") || norm.includes("6bedroomterraceduple") || (norm.includes("6bedroom") && norm.includes("terrace"))) return "/assets/media/zangon/6bedroomterraceduple.jpg";
  return HOUSE_TYPE_IMAGES[typeName] || "/assets/media/zangon/3bedroomsbungalo.jpg";
}

function getDueMeta(today, expiryISO) {
  const d = daysBetween(today, parseISODate(expiryISO));
  const plural = (n) => `${n} day${n === 1 ? "" : "s"}`;
  if (d < 0) return { badgeText: "Overdue", badgeClass: "badge-light-danger", expiryClass: "text-danger", days: `${plural(Math.abs(d))} overdue` };
  if (d <= 30) return { badgeText: "Due Soon", badgeClass: "badge-light-warning", expiryClass: "text-warning", days: `${plural(d)} left` };
  return { badgeText: "Not Due", badgeClass: "badge-light-success", expiryClass: "text-success", days: `${plural(d)} left` };
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function getInitials(first = "", last = "") {
  if (first && last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  }
  const clean = (first || last || "").replace(/^(mr|mrs|ms|miss|dr|prof|engr|arch|barr|alh|alhaji)\.?\s+/i, "").trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }
  if (parts.length === 1 && parts[0].length >= 2) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (clean.slice(0, 2) || "??").toUpperCase();
}

function useBootstrapModal(ref) {
  const show = useCallback(() => {
    if (!ref.current || !window.bootstrap?.Modal) return;
    window.bootstrap.Modal.getOrCreateInstance(ref.current, { backdrop: "static" }).show();
  }, [ref]);
  const hide = useCallback(() => {
    if (!ref.current || !window.bootstrap?.Modal) return;
    window.bootstrap.Modal.getInstance(ref.current)?.hide();
  }, [ref]);
  return { show, hide };
}

function PagePreloader({ visible }) {
  return (
    <div
      className={`page-preloader-overlay d6-topnav-preloader ${visible ? "is-visible" : ""}`}
      aria-hidden={!visible}
    >
      <div className="logo-box">
        <svg className="house-icon" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <path d="M 34 60 L 34 80 L 18 80 L 18 50 L 34 34 L 50 50 L 50 85" fill="none" stroke="#FFFFFF" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
          <path d="M 45.31 22.69 L 66 43.37 L 66 85" fill="none" stroke="#BBEE6B" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
          <path d="M 56.63 11.37 L 82 36.75 L 82 85" fill="none" stroke="#FFD75B" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
        </svg>
      </div>
    </div>
  );
}

const SUCCESS_DOTS = [
  { top: "18%", left: "14%", size: 6, delay: 0 },
  { top: "70%", left: "10%", size: 5, delay: 0.3 },
  { top: "82%", left: "34%", size: 4, delay: 0.6 },
  { top: "12%", left: "80%", size: 5, delay: 0.2 },
  { top: "74%", left: "88%", size: 6, delay: 0.5 },
  { top: "90%", left: "62%", size: 4, delay: 0.8 },
];

function SuccessState({ title, message, duration = 2400, onDone }) {
  const doneRef = useRef(onDone);
  useEffect(() => {
    doneRef.current = onDone;
  });
  useEffect(() => {
    const t = setTimeout(() => doneRef.current?.(), duration);
    return () => clearTimeout(t);
  }, [duration]);

  return (
    <div className="d6-success" role="status" aria-live="polite">
      <div className="d6-success-burst">
        <span className="d6-success-ring d6-success-ring-lg" />
        <span className="d6-success-ring d6-success-ring-md" />
        {SUCCESS_DOTS.map((dot, i) => (
          <span
            key={i}
            className="d6-success-dot"
            style={{ top: dot.top, left: dot.left, width: dot.size, height: dot.size, animationDelay: `${dot.delay}s` }}
          />
        ))}
        <svg viewBox="0 0 120 120" className="d6-success-check" aria-hidden="true">
          <circle cx="60" cy="60" r="52" className="d6-success-halo" />
          <circle cx="60" cy="60" r="44" className="d6-success-fill" />
          <circle cx="60" cy="60" r="44" className="d6-success-stroke" />
          <path d="M40 61 L54 75 L81 46" className="d6-success-tick" />
        </svg>
      </div>
      <h3 className="d6-success-title">{title}</h3>
      {message && <p className="d6-success-msg">{message}</p>}
      <div className="d6-success-progress">
        <span style={{ animationDuration: `${duration}ms` }} />
      </div>
    </div>
  );
}

function ModalShell({ modalRef, icon, title, onClose, closeDisabled, subheader, footer, overlay, width = 720, fill = false, children }) {
  return (
    <div className="modal fade" ref={modalRef} tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered d6m-dialog" style={{ maxWidth: width }}>
        <div className="modal-content d6m">
          {overlay}
          <div className="d6m-head">
            <span className="d6m-head-icon"><i className={`ki-outline ${icon}`}></i></span>
            <div className="min-w-0 flex-grow-1">
              <h3 className="d6m-title text-truncate">{title}</h3>
            </div>
            <button type="button" className="d6m-x" onClick={onClose} disabled={closeDisabled} aria-label="Close">
              <i className="ki-outline ki-cross"></i>
            </button>
          </div>
          {subheader}
          <div className={`d6m-body ${fill ? "d6m-body-fill" : ""}`}>{children}</div>
          {footer && <div className="d6m-foot">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

function StatusChip({ color = "#a1a1aa", children }) {
  return (
    <span className="d6m-chip" style={{ "--dot": color }}>
      {children}
    </span>
  );
}

function Avatar({ src, name, size = 48, onClick }) {
  const initials = getInitials(name || "");
  return (
    <span
      className={`d6m-avatar ${onClick ? "cursor-pointer" : ""}`}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.34) }}
      onClick={onClick}
      title={onClick ? "View photo" : undefined}
    >
      {src ? <img src={src} alt="" /> : initials}
    </span>
  );
}

const Shimmer = ({ w = "100%", h = 12, r = 6, className = "" }) => (
  <span className={`d6m-shimmer ${className}`} style={{ width: w, height: h, borderRadius: r }} />
);

function SegTabs({ options, value, onChange }) {
  return (
    <div className="d6-seg">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`fs-8 fw-bold ${value === o.value ? "active" : ""}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function CardHead({ title, subtitle, children, className = "mb-4" }) {
  return (
    <div className={`d-flex align-items-start justify-content-between flex-wrap gap-3 ${className}`}>
      <div className="min-w-0">
        <h3 className="text-gray-900 d6-title mb-1">{title}</h3>
        {subtitle && <span className="text-gray-400 fs-8">{subtitle}</span>}
      </div>
      {children && <div className="d-flex align-items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}



// ─── Helpers ───────────────────────────────────────────────────
const naira = (n) => `₦${Math.round(Number(n) || 0).toLocaleString()}`;

function normalizeInvoice(inv) {
  const total = Number(inv.totalAmount ?? inv.invoiceTotalAmount ?? inv.rentAmount ?? inv.amountDue ?? inv.amount ?? 0);
  const paid = Number(inv.amountPaid || 0);
  const rawStatus = (inv.status || "").toLowerCase();
  const isPaid = rawStatus === "paid" || rawStatus === "completed" || (total > 0 && paid >= total);
  const status = isPaid ? "Paid" : (rawStatus.includes("overdue") ? "Overdue" : "Pending");
  return {
    id: inv.rentInvoiceId || inv.id,
    buildingId: inv.buildingId,
    home: inv.buildingName || (inv.number ? `House ${inv.number}` : "Estate Home"),
    phaseName: inv.phaseName || "",
    period: inv.period || (inv.periodStart ? `${fmtDate(inv.periodStart)} - ${fmtDate(inv.periodEnd)}` : "Rent Invoice"),
    dueDate: inv.dueDate || inv.createdDate,
    total,
    paid: isPaid ? Math.max(paid, total) : paid,
    balance: isPaid ? 0 : Math.max(0, total - paid),
    status,
    isPaid,
    components: Array.isArray(inv.components) ? inv.components : [],
  };
}

function normalizeHome(b, i) {
  const rent = b.currentRent || b.activeRent || {};
  return {
    id: b.buildingId || b.id || `home-${i}`,
    buildingId: b.buildingId || b.id,
    number: b.number ? (String(b.number).startsWith("Staff Quarter") ? b.number : `House ${b.number}`) : (b.buildingName || b.name || "Residence"),
    phaseName: b.phaseName || "",
    typeName: b.typeName || b.componentTypeLabel || "Residential Unit",
    assignedAt: b.assignedAt,
    nextDueDate: rent.nextDueDate || b.nextDueDate,
    nextAmount: Number(rent.estimatedInvoiceTotal ?? rent.rentAmount ?? b.rentAmount ?? 0),
    image: b.imageUrl || getHouseImage(b.typeName),
  };
}

const INVOICE_DOT = { Paid: "#16A34A", Pending: "#D97706", Overdue: "#DC2626" };
const REQUEST_BAR = { Open: "bg-warning", "In progress": "bg-primary", Resolved: "bg-success", Canceled: "bg-secondary" };
const REQUEST_TEXT = { Open: "text-warning", "In progress": "text-primary", Resolved: "text-success", Canceled: "text-gray-500" };

// ─── Modals ────────────────────────────────────────────────────
function InvoiceModal({ modalRef, invoice, today, onClose, onPay }) {
  const { hide } = useBootstrapModal(modalRef);
  const handleClose = () => { hide(); onClose?.(); };
  const due = invoice?.dueDate && !invoice.isPaid ? getDueMeta(today, String(invoice.dueDate).split("T")[0]) : null;

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-bill"
      title="Rent invoice"
      onClose={handleClose}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={handleClose}>Close</button>
          {invoice && !invoice.isPaid && (
            <button type="button" className="btn btn-success d6-mbtn" onClick={() => { handleClose(); onPay?.(invoice); }}>
              Pay {naira(invoice.balance)}
            </button>
          )}
        </>
      }
    >
      {!invoice ? (
        <>
          <div className="d6m-person">
            <Shimmer w={48} h={48} r={999} />
            <div className="flex-grow-1 d-flex flex-column gap-2"><Shimmer w="45%" h={14} /><Shimmer w="30%" h={10} /></div>
          </div>
          <div className="d6m-invoice mt-5"><Shimmer w="40%" h={18} /><Shimmer w="25%" h={10} className="mt-2" /></div>
        </>
      ) : (
        <>
          <div className="d6m-person">
            <Avatar src={getHouseImage(invoice.home)} name={invoice.home} />
            <div className="min-w-0 flex-grow-1">
              <div className="d6m-name text-truncate">{invoice.home}</div>
              <div className="d6m-muted text-truncate">{invoice.period ? `Billing period · ${invoice.period}` : "Rent invoice"}</div>
            </div>
            <StatusChip color={INVOICE_DOT[invoice.status] || "#D97706"}>{invoice.status}</StatusChip>
          </div>

          <div className="d6m-kv mt-5">
            <div className="d6m-kv-cell">
              <span className="d6m-k">Due date</span>
              <span className="d6m-v">{fmtDate(invoice.dueDate)}</span>
            </div>
            <div className="d6m-kv-cell">
              <span className="d6m-k">{invoice.isPaid ? "Paid" : "Time left"}</span>
              <span className="d6m-v">{invoice.isPaid ? naira(invoice.paid) : due ? due.days : "—"}</span>
            </div>
          </div>

          <div className="d6m-section">
            <div className="d6m-section-head"><span>Breakdown</span></div>
            <div className="d6m-invoice">
              <div className="d-flex align-items-start justify-content-between gap-3">
                <div>
                  <div className="d6m-amount">{naira(invoice.total)}</div>
                  <div className="d6m-muted mt-1">Invoice total</div>
                </div>
              </div>
              <div className="d6m-lines">
                {invoice.components.map((c, i) => (
                  <div key={i} className="d6m-line">
                    <span>{c.displayName || c.name || c.type || "Charge"}</span>
                    <span>{naira(c.amount)}</span>
                  </div>
                ))}
                <div className="d6m-line d6m-line-total"><span>Total</span><span>{naira(invoice.total)}</span></div>
                {invoice.paid > 0 && <div className="d6m-line"><span>Paid</span><span>− {naira(invoice.paid)}</span></div>}
                <div className="d6m-line d6m-line-total"><span>Balance</span><span>{naira(invoice.balance)}</span></div>
              </div>
            </div>
          </div>
        </>
      )}
    </ModalShell>
  );
}

function Field({ label, required, hint, error, children, className = "" }) {
  return (
    <div className={`d6m-field ${className}`}>
      <label>
        {label}
        {required && <span className="text-danger ms-1">*</span>}
      </label>
      {children}
      {error ? <div className="d6m-error">{error}</div> : hint ? <div className="d6m-hint">{hint}</div> : null}
    </div>
  );
}

const PRIORITIES = ["Low", "Medium", "High", "Critical"];
const BLANK_ISSUE = { homeId: "", categoryId: "", title: "", description: "", priority: "Medium" };

function ReportIssueModal({ modalRef, homes, categories, onSubmitted }) {
  const { hide } = useBootstrapModal(modalRef);
  const [form, setForm] = useState(BLANK_ISSUE);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);

  const reset = () => { setForm(BLANK_ISSUE); setErrors({}); setSuccess(null); };
  const close = () => { hide(); reset(); };

  const handleSubmit = async () => {
    const e = {};
    const homeId = form.homeId || homes[0]?.id || "";
    if (!homeId) e.homeId = "Choose the home this is about";
    if (!form.categoryId) e.categoryId = "Choose a category";
    if (!form.title.trim()) e.title = "Give the issue a short title";
    if (!form.description.trim()) e.description = "Describe what's wrong";
    setErrors(e);
    if (Object.keys(e).length) return;

    setSaving(true);
    try {
      const res = await submitMaintenanceRequest({
        title: capitalizeFirst(form.title.trim()),
        description: capitalizeFirst(form.description.trim()),
        categoryId: form.categoryId,
        priorityLevel: form.priority,
        buildingId: homeId,
      });

      const home = homes.find((h) => h.id === homeId);
      const category = categories.find((c) => c.id === form.categoryId);
      onSubmitted?.({
        id: res?.id || `req-${Date.now()}`,
        title: capitalizeFirst(form.title.trim()),
        description: capitalizeFirst(form.description.trim()),
        category: category?.name || "General",
        priority: form.priority,
        status: "Open",
        createdDate: new Date().toISOString(),
        home: home ? `${home.phaseName} — ${home.number}` : "",
        uploadedFileUrls: [],
      });
      setSuccess({ title: "Request sent", message: "The estate team has your request. You'll see their updates in the conversation." });
    } catch (err) {
      toast.error(err.message || "Could not submit your request. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = (key) => `d6m-input${errors[key] ? " is-invalid" : ""}`;

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-wrench"
      title="Report an issue"
      onClose={close}
      closeDisabled={saving}
      overlay={success && <SuccessState title={success.title} message={success.message} duration={2400} onDone={close} />}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={close} disabled={saving}>Cancel</button>
          <button type="button" className="btn btn-success d6-mbtn" onClick={handleSubmit} disabled={saving}>
            {saving ? <><span className="spinner-border spinner-border-sm me-2"></span>Sending…</> : "Send request"}
          </button>
        </>
      }
    >
      <div className="row g-4">
        <Field className="col-md-6" label="Home" required error={errors.homeId}>
          <select className={inputCls("homeId")} value={form.homeId || homes[0]?.id || ""} onChange={(e) => setForm((p) => ({ ...p, homeId: e.target.value }))}>
            {homes.map((h) => <option key={h.id} value={h.id}>{h.phaseName} — {h.number}</option>)}
          </select>
        </Field>
        <Field className="col-md-6" label="Category" required error={errors.categoryId}>
          <select className={inputCls("categoryId")} value={form.categoryId} onChange={(e) => setForm((p) => ({ ...p, categoryId: e.target.value }))}>
            <option value="">Choose a category…</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field className="col-12" label="Title" required error={errors.title}>
          <input className={inputCls("title")} placeholder="e.g. Kitchen tap is leaking" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} />
        </Field>
        <Field className="col-12" label="What's happening?" required error={errors.description} hint="Include where it is and when it started.">
          <textarea className={inputCls("description")} rows="4" placeholder="Describe the problem…" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
        </Field>
        <Field className="col-12" label="How urgent is it?">
          <div className="d6m-seg">
            {PRIORITIES.map((p) => (
              <button key={p} type="button" className={form.priority === p ? "active" : ""} onClick={() => setForm((f) => ({ ...f, priority: p }))}>
                <span className="d6m-dot" style={{ background: PRIORITY_DOT[p] }} />
                {p}
              </button>
            ))}
          </div>
        </Field>
      </div>
    </ModalShell>
  );
}

const SYSTEM_SEARCH_CATEGORIES = [
  { id: "all", label: "All results", icon: "ki-magnifier" },
  { id: "pages", label: "Pages & tools", icon: "ki-element-11" },
  { id: "homes", label: "Homes", icon: "ki-home-2" },
  { id: "people", label: "People & access", icon: "ki-people" },
  { id: "vehicles", label: "Vehicles", icon: "ki-car" },
  { id: "payments", label: "Payments", icon: "ki-bill" },
  { id: "maintenance", label: "Maintenance", icon: "ki-wrench" },
];

const SYSTEM_SEARCH_PAGES = [
  { id: "page-dashboard", category: "pages", title: "My Dashboard", subtitle: "Overview, activity and quick actions", route: "/tenant/dashboard", icon: "ki-element-11" },
  { id: "page-homes", category: "pages", title: "My Homes", subtitle: "Assigned properties and rent details", route: "/tenant/buildings", icon: "ki-home-2" },
  { id: "page-household", category: "pages", title: "My Household", subtitle: "Dependants, residents and access", route: "/tenant/dependants", icon: "ki-people" },
  { id: "page-vehicles", category: "pages", title: "My Vehicles", subtitle: "Vehicle registrations", route: "/tenant/cars", icon: "ki-car" },
  { id: "page-payments", category: "pages", title: "My Payments", subtitle: "Invoices and payment history", route: "/tenant/payments", icon: "ki-bill" },
  { id: "page-maintenance", category: "pages", title: "My Maintenance", subtitle: "Requests and repair updates", route: "/tenant/maintenance", icon: "ki-wrench" },
  { id: "page-access", category: "pages", title: "Access Codes", subtitle: "Manage household and visitor access", route: "/tenant/access-codes", icon: "ki-key" },
  { id: "page-profile", category: "pages", title: "My Profile", subtitle: "Personal information and security", route: "/tenant/profile", icon: "ki-user" },
];

function searchText(value) {
  if (value == null) return "";
  if (Array.isArray(value)) return value.map(searchText).join(" ");
  if (typeof value === "object") return Object.values(value).map(searchText).join(" ");
  return String(value);
}

function GlobalSearchModal({ activeQuery, category, setCategory, results, onClose, onSelect }) {
  const queryReady = activeQuery.trim().length > 0;
  const categoryLabel = SYSTEM_SEARCH_CATEGORIES.find((item) => item.id === category)?.label || "All results";

  return (
    <div className="d6-global-search-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="d6-global-search-modal" role="dialog" aria-modal="true" aria-labelledby="d6-search-title">
        <aside className="d6-global-search-sidebar">
          <div className="d6-global-search-brand"><span className="d6-global-search-brand-icon"><i className="ki-outline ki-magnifier" /></span><span>System Search</span></div>
          <nav className="d6-global-search-categories" aria-label="Search categories">
            {SYSTEM_SEARCH_CATEGORIES.map((item) => (
              <button key={item.id} type="button" className={category === item.id ? "active" : ""} onClick={() => setCategory(item.id)}>
                <i className={`ki-outline ${item.icon}`} /><span>{item.label}</span><i className="ki-outline ki-right d6-global-search-chevron" />
              </button>
            ))}
          </nav>
          <div className="d6-global-search-sidebar-foot"><span>Search across your workspace</span><span>Homes · people · payments · requests</span></div>
        </aside>

        <div className="d6-global-search-main">
          <header className="d6-global-search-header">
            <div className="min-w-0"><h2 id="d6-search-title">Search result</h2></div>
            <button type="button" className="d6-global-search-close" onClick={onClose} aria-label="Close search"><i className="ki-outline ki-cross" /></button>
          </header>
          <div className="d6-global-search-results-head"><span>{queryReady ? `${results.length} ${results.length === 1 ? "result" : "results"} for “${activeQuery}”` : "No search term submitted"}</span><span>{categoryLabel}</span></div>
          <div className="d6-global-search-results" role="listbox" aria-label="Search results">
            {results.length ? results.map((item) => (
              <button key={item.id} type="button" className="d6-global-search-result" onClick={() => onSelect(item)} role="option" aria-selected="false">
                <span className={`d6-global-search-result-icon ${item.category}`}><i className={`ki-outline ${item.icon}`} /></span>
                <span className="d6-global-search-result-copy"><strong>{item.title}</strong><small>{item.subtitle}</small></span>
                <span className="d6-global-search-result-type">{item.typeLabel}</span><i className="ki-outline ki-arrow-up-right d6-global-search-result-arrow" />
              </button>
            )) : (
              <div className="d6-global-search-empty"><span><i className="ki-outline ki-magnifier" /></span><strong>{queryReady ? "No matches found" : "No quick links in this category"}</strong><small>{queryReady ? "Try another name, reference, address, plate number or status." : "Choose another category or search again from the top bar."}</small></div>
            )}
          </div>
          <footer className="d6-global-search-footer"><span><kbd>⌘</kbd><kbd>K</kbd> Open search</span><span>Press Esc to close</span></footer>
        </div>
      </section>
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────
export default function NewDashboard() {
  const navigate = useNavigate();
  const { setHeaderConfig } = useTenantHeader();
  const dashboardRef = useRef(null);
  const tenantId = getTenantId();
  const session = getTenantSession() || {};
  const greeting = useMemo(() => getGreeting(), []);
  const today = useMemo(() => new Date(), []);

  const [pageReady, setPageReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState({ profile: null, homes: [], invoices: [], payments: [], requests: [], dependants: [], cars: [], codes: [] });
  const [overviewTab, setOverviewTab] = useState("payments");
  const [homeIndex, setHomeIndex] = useState(0);
  const [homeAnimKey, setHomeAnimKey] = useState(0);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [globalSearch, setGlobalSearch] = useState("");
  const [activeSearchQuery, setActiveSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchCategory, setSearchCategory] = useState("all");
  const headerSearchRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    setHeaderConfig({
      searchComponent: (
        <form
          className="d6-topnav-search"
          role="search"
          onSubmit={(event) => {
            event.preventDefault();
            const submittedQuery = globalSearch.trim();
            if (!submittedQuery) return;
            setActiveSearchQuery(submittedQuery);
            setSearchOpen(true);
          }}
        >
          <i className="ki-outline ki-magnifier" aria-hidden="true"></i>
          <input
            ref={headerSearchRef}
            type="search"
            aria-label="Search the system"
            placeholder="Search homes, people, payments..."
            value={globalSearch}
            onChange={(event) => setGlobalSearch(event.target.value)}
          />
          <kbd>⌘ K</kbd>
          {globalSearch.trim() && (
            <button
              type="submit"
              className="d6-topnav-search-go"
              aria-label="Go to search"
              title="Search"
            >
              <svg viewBox="0 0 20 20" aria-hidden="true">
                <path d="M3.5 10h12m-5-5 5 5-5 5" />
              </svg>
            </button>
          )}
        </form>
      ),
    });
    return () => setHeaderConfig({ title: "", breadcrumb: "", actions: null, searchComponent: null });
  }, [globalSearch, setHeaderConfig]);

// ─── Quick Access Pass State ─────────────────────────────────────
const [passForm, setPassForm] = useState({
  isPermanent: false,
  personType: "Visitor", // Visitor | Delivery Person | Service Person | Family Member | Staff
  fullName: "",
  phoneNumber: "",
  buildingId: "",
  durationDays: "1",
  customDate: "",
});
const [passLoading, setPassLoading] = useState(false);
const [passError, setPassError] = useState("");
const [activePassData, setActivePassData] = useState(null);

const accessPassModalRef = useRef(null);
const accessPassModal = useBootstrapModal(accessPassModalRef);

const PERSON_TYPES = [
  "Visitor",
  "Delivery Person",
  "Service Person",
  "Family Member",
  "Staff",
];
const DURATION_PRESETS = [
  { label: "1 Day", val: "1" },
  { label: "2 Days", val: "2" },
  { label: "3 Days", val: "3" },
  { label: "7 Days", val: "7" },
  { label: "30 Days", val: "30" },
  { label: "Custom", val: "custom" },
];

const handleSelectSavedContact = (contact) => {
  setPassForm((prev) => ({
    ...prev,
    fullName: contact.fullName || `${contact.firstName || ""} ${contact.lastName || ""}`.trim(),
    phoneNumber: contact.phoneNumber || contact.phone || "",
    personType: contact.relationship || contact.category || "Visitor",
    isPermanent: Boolean(contact.isPermanent),
  }));
  setPassError("");
};

const handleGeneratePass = async (e) => {
  e?.preventDefault();
  setPassError("");

  if (!passForm.fullName.trim()) {
    setPassError("Please provide a contact name.");
    return;
  }
  if (!passForm.phoneNumber.trim()) {
    setPassError("Please enter a valid phone number.");
    return;
  }

  const selectedBuildingId = passForm.buildingId || homes[0]?.id || "";
  if (!selectedBuildingId) {
    setPassError("Please select a target home.");
    return;
  }

  setPassLoading(true);
  try {
    const fullName = passForm.fullName.trim();
    const [firstName, ...rest] = fullName.split(/\s+/);
    const validDays = passForm.isPermanent
      ? null
      : passForm.durationDays === "custom"
      ? daysBetween(new Date(), new Date(passForm.customDate || Date.now()))
      : Number(passForm.durationDays);

    const res = await createDependentWithAccessCode({
      firstName,
      lastName: rest.join(" ") || firstName,
      phoneNumber: passForm.phoneNumber.trim(),
      dependentType: passForm.personType,
      isAccessCodePermanent: passForm.isPermanent,
      durationValue: validDays || 1,
      durationUnit: 2,
      buildingId: selectedBuildingId,
    }, tenantId);

    const passResult = {
      fullName,
      personType: passForm.personType,
      isPermanent: passForm.isPermanent,
      validDays,
      code: res?.code,
      expiresAt: res?.expiresAt,
      qrCodeFileUrl: res?.qrCodeFileUrl,
      qrCodeData: res?.code,
      buildingName: homes.find((h) => h.id === selectedBuildingId)?.number || "Estate Home",
    };

    setActivePassData(passResult);
    accessPassModal.show();

    // Reset form after success
    setPassForm({
      isPermanent: false,
      personType: "Visitor",
      fullName: "",
      phoneNumber: "",
      buildingId: homes[0]?.id || "",
      durationDays: "1",
      customDate: "",
    });
  } catch (err) {
    setPassError(err.message || "Failed to issue gate access pass.");
  } finally {
    setPassLoading(false);
  }
};


  const invoiceModalRef = useRef(null);
  const requestModalRef = useRef(null);
  const reportModalRef = useRef(null);
  const invoiceModal = useBootstrapModal(invoiceModalRef);
  const requestModal = useBootstrapModal(requestModalRef);
  const reportModal = useBootstrapModal(reportModalRef);

  const fetchDashboardData = useCallback(() => {
    if (!tenantId) {
      setLoading(false);
      setPageReady(true);
      return;
    }
    const isPo = isPrimaryOccupant();
    const detailsUrl = isPo
      ? `${API_BASE}/primary-occupants/${tenantId}/details`
      : `${API_BASE}/tenants/${tenantId}/details`;
    const invoicesUrl = isPo
      ? null
      : `${API_BASE}/tenants/${tenantId}/rent-invoices?pageIndex=1&pageSize=50`;
    const rootPath = isPo ? `primary-occupants/${tenantId}` : `tenants/${tenantId}`;
    const maintUrl = `${API_BASE}/${rootPath}/maintenance-requests?pageIndex=1&pageSize=20`;

    Promise.allSettled([
      tenantFetch(detailsUrl).then((r) => (r.ok ? r.json() : null)).then(applyBillingEnumText),
      getAssignedBuildings({ id: tenantId }).then((data) => ({ data })),
      invoicesUrl ? tenantFetch(invoicesUrl).then((r) => (r.ok ? r.json() : null)).then(applyBillingEnumText) : Promise.resolve(null),
      tenantFetch(maintUrl).then((r) => (r.ok ? r.json() : null)),
      getDependentsWithAccess({ pageIndex: 0, pageSize: 50, id: tenantId }).catch(() => null),
      getCars(tenantId).catch(() => null),
      getMaintenanceCategories(tenantId).catch(() => null),
      isPo ? Promise.resolve(null) : getPaymentHistory({ pageIndex: 0, pageSize: 50, id: tenantId }).catch(() => null),
    ]).then(async ([tRes, bRes, invRes, maintRes, depsRes, cRes, catRes, payRes]) => {
      const rawPayments = payRes.status === "fulfilled" && payRes.value ? parseArrayData({ data: payRes.value }) : [];
      if (catRes.status === "fulfilled" && catRes.value) setCategories(parseArrayData({ data: catRes.value }));
      const tenantData = tRes.status === "fulfilled" && tRes.value ? (tRes.value?.data || tRes.value) : null;
      const rawBuildings = bRes.status === "fulfilled" && bRes.value ? parseArrayData(bRes.value) : [];
      const rawInvoices = invRes.status === "fulfilled" && invRes.value ? parseArrayData(invRes.value) : [];
      const rawMaintenance = maintRes.status === "fulfilled" && maintRes.value ? parseArrayData(maintRes.value).map((r) => applyEnumText(r, ["status", "priorityLevel"])) : [];
      const rawDeps = depsRes.status === "fulfilled" && depsRes.value ? parseArrayData(depsRes.value) : [];
      const rawCars = cRes.status === "fulfilled" && cRes.value ? parseArrayData(cRes.value) : [];

      // Fetch active-rent details for buildings if available
      const buildingsWithRent = await Promise.all(
        rawBuildings.map(async (b, idx) => {
          const bId = b.buildingId || b.id;
          if (!bId) return normalizeHome(b, idx);
          try {
            const rentRes = await tenantFetch(`${API_BASE}/${rootPath}/buildings/${bId}/active-rent`);
            if (rentRes.ok) {
              const rentJson = applyBillingEnumText(await rentRes.json());
              b.currentRent = rentJson?.data || null;
            }
          } catch {
            // fallback gracefully
          }
          return normalizeHome(b, idx);
        })
      );

      // Merge tenant outstanding invoices if present
      const invoiceMap = new Map();
      if (Array.isArray(tenantData?.outstandingInvoices)) {
        tenantData.outstandingInvoices.forEach(inv => {
          if (inv?.id || inv?.rentInvoiceId) {
            invoiceMap.set(inv.id || inv.rentInvoiceId, normalizeInvoice(inv));
          }
        });
      }
      rawInvoices.forEach(inv => {
        const key = inv.id || inv.rentInvoiceId;
        if (key && !invoiceMap.has(key)) {
          invoiceMap.set(key, normalizeInvoice(inv));
        }
      });
      const parsedInvoices = Array.from(invoiceMap.values());

      setData({
        profile: tenantData,
        homes: buildingsWithRent,
        invoices: parsedInvoices,
        payments: rawPayments
          .filter(p => String(p.status || p.paymentStatus || "successful").toLowerCase() === "successful")
          .map(p => ({
            id: p.id || p.rentPaymentId || p.paymentId,
            paidAt: p.paidAt || p.paymentDate || p.dueDate,
            amount: Number(p.amountPaid ?? p.amount ?? 0),
            home: p.buildingNumber ? `House ${p.buildingNumber}` : p.buildingName || "Estate Home",
          })),
        requests: rawMaintenance.map(normalizeRequest),
        dependants: rawDeps,
        cars: rawCars,
        codes: rawDeps.map(d => d.accessCode || d.code).filter(Boolean),
      });
    }).finally(() => {
      setLoading(false);
      setPageReady(true);
    });
  }, [tenantId]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const greetingProfile = data.profile || {};
  const greetingTitle = greetingProfile.title || session.title;
  const greetingFullName = greetingProfile.fullName || [
    greetingProfile.firstName || session.firstName,
    greetingProfile.lastName || session.lastName,
  ].filter(Boolean).join(" ") || "there";
  const greetingName = greetingTitle && !greetingFullName.toLowerCase().startsWith(`${greetingTitle.toLowerCase()} `)
    ? `${greetingTitle} ${greetingFullName}`
    : greetingFullName;

  useEffect(() => {
    const body = document.body;
    body.id = "kt_app_body";
    body.classList.add("app-default");
    body.removeAttribute("data-kt-app-header-fixed-mobile");
    body.removeAttribute("data-kt-app-sidebar-enabled");
    body.removeAttribute("data-kt-app-sidebar-fixed");
    body.removeAttribute("data-kt-app-sidebar-push-header");
    body.removeAttribute("data-kt-app-sidebar-push-footer");
    body.removeAttribute("data-kt-app-sidebar-push-toolbar");
    document.documentElement.setAttribute("data-bs-theme", localStorage.getItem("kt_theme_mode") || "light");
    const init = () => {
      window.KTComponents?.init?.();
      window.KTMenu?.createInstances?.();
      window.KTDrawer?.createInstances?.();
      window.KTScrollTop?.createInstances?.();
    };
    init();
    const t = setTimeout(init, 0);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!pageReady) return undefined;
    const ctx = gsap.context(() => {
      gsap.fromTo(".gsap-header", { opacity: 0, y: -12 }, { opacity: 1, y: 0, duration: 0.5, ease: "power2.out", clearProps: "all" });
      gsap.fromTo(".gsap-hero", { opacity: 0, y: 18 }, { opacity: 1, y: 0, stagger: 0.06, duration: 0.55, ease: "power3.out", delay: 0.05, clearProps: "all" });
      gsap.fromTo(".gsap-card", { opacity: 0, y: 18 }, { opacity: 1, y: 0, stagger: 0.08, duration: 0.55, ease: "power2.out", delay: 0.2, clearProps: "all" });
    });
    return () => ctx.revert();
  }, [pageReady]);

  const { homes, invoices, payments, requests, dependants, cars, codes } = data;

  const globalSearchResults = useMemo(() => {
    const profile = data.profile;
    const profileName = profile?.fullName || [profile?.firstName, profile?.lastName].filter(Boolean).join(" ") || "My profile";
    const items = [
      ...SYSTEM_SEARCH_PAGES.map((page) => ({ ...page, typeLabel: "Page", searchValue: `${page.title} ${page.subtitle} ${page.route}` })),
      ...(profile ? [{ id: "profile-record", category: "people", title: profileName, subtitle: [profile.email, profile.phoneNumber].filter(Boolean).join(" · ") || "Profile and account details", route: "/tenant/profile", icon: "ki-user", typeLabel: "Profile", searchValue: searchText(profile) }] : []),
      ...homes.map((home, index) => ({ id: `home-${home.id || index}`, category: "homes", title: home.number || home.typeName || "Home", subtitle: [home.phaseName, home.typeName, home.nextDueDate ? `Next due ${fmtDate(home.nextDueDate)}` : ""].filter(Boolean).join(" · "), route: "/tenant/buildings", icon: "ki-home-2", typeLabel: "Home", searchValue: searchText(home) })),
      ...dependants.map((person, index) => {
        const personName = person.fullName || [person.title, person.firstName, person.lastName].filter(Boolean).join(" ") || person.name || "Household member";
        return { id: `person-${person.id || person.dependentId || index}`, category: "people", title: personName, subtitle: [person.relationship || person.designation || person.role, person.buildingName || person.houseNumber, person.accessCode || person.code].filter(Boolean).join(" · ") || "Household member", route: "/tenant/dependants", icon: "ki-people", typeLabel: "Person", searchValue: searchText(person) };
      }),
      ...cars.map((car, index) => ({ id: `vehicle-${car.id || index}`, category: "vehicles", title: [car.manufacturer, car.make, car.model].filter(Boolean).join(" ") || "Vehicle", subtitle: [car.licensePlate || car.plateNumber, car.color, car.year].filter(Boolean).join(" · ") || "Registered vehicle", route: "/tenant/cars", icon: "ki-car", typeLabel: "Vehicle", searchValue: searchText(car) })),
      ...invoices.map((invoice, index) => ({ id: `invoice-${invoice.id || index}`, category: "payments", title: `${invoice.home || "Estate Home"} invoice`, subtitle: [invoice.period, invoice.status, invoice.dueDate ? `Due ${fmtDate(invoice.dueDate)}` : "", naira(invoice.balance ?? invoice.total)].filter(Boolean).join(" · "), route: "/tenant/payments", icon: "ki-bill", typeLabel: "Invoice", searchValue: searchText(invoice) })),
      ...payments.map((payment, index) => ({ id: `payment-${payment.id || index}`, category: "payments", title: `${payment.home || "Estate Home"} payment`, subtitle: [payment.paidAt ? fmtDate(payment.paidAt) : "", naira(payment.amount)].filter(Boolean).join(" · "), route: "/tenant/payments", icon: "ki-wallet", typeLabel: "Payment", searchValue: searchText(payment) })),
      ...requests.map((request, index) => ({ id: `request-${request.id || index}`, category: "maintenance", title: request.title || "Maintenance request", subtitle: [request.category, request.status, request.home].filter(Boolean).join(" · "), route: "/tenant/maintenance", icon: "ki-wrench", typeLabel: "Request", searchValue: searchText(request) })),
      ...codes.map((code, index) => ({ id: `access-code-${index}`, category: "people", title: String(code), subtitle: "Household access code", route: "/tenant/access-codes", icon: "ki-key", typeLabel: "Access code", searchValue: String(code) })),
    ];
    const query = activeSearchQuery.trim().toLowerCase();
    return items.filter((item) => {
      const categoryMatches = searchCategory === "all" || item.category === searchCategory;
      const queryMatches = !query || `${item.title} ${item.subtitle} ${item.searchValue}`.toLowerCase().includes(query);
      return categoryMatches && queryMatches;
    }).slice(0, query ? 60 : 8);
  }, [data.profile, homes, invoices, payments, requests, dependants, cars, codes, activeSearchQuery, searchCategory]);

  useEffect(() => {
    const handleSearchShortcut = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        if (searchOpen) searchInputRef.current?.focus();
        else headerSearchRef.current?.focus();
      }
      if (event.key === "Escape") setSearchOpen(false);
    };
    document.addEventListener("keydown", handleSearchShortcut);
    return () => document.removeEventListener("keydown", handleSearchShortcut);
  }, [searchOpen]);

  useEffect(() => {
    if (homes.length < 2) return undefined;
    const id = setInterval(() => {
      setHomeIndex((i) => (i + 1) % homes.length);
      setHomeAnimKey((k) => k + 1);
    }, 10000);
    return () => clearInterval(id);
  }, [homes.length]);

  const unpaid = useMemo(
    () => invoices.filter((i) => !i.isPaid).sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate)),
    [invoices]
  );
  const billedTotal = invoices.reduce((a, i) => a + i.total, 0);
  const paidTotal = invoices.reduce((a, i) => a + Math.min(i.paid, i.total), 0);
  const outstanding = unpaid.reduce((a, i) => a + i.balance, 0);
  const paidPct = billedTotal ? Math.round((paidTotal / billedTotal) * 100) : 0;
  const activeRequests = requests.filter((r) => r.status === "Open" || r.status === "In progress");
  const listedRequests = [...activeRequests, ...requests.filter((r) => r.status !== "Open" && r.status !== "In progress")].slice(0, 8);
  const hasPendingRentOrRequest = unpaid.length > 0 || activeRequests.length > 0;

  useEffect(() => {
    if (unpaid.length === 0 && activeRequests.length > 0) {
      setOverviewTab("requests");
    } else if (unpaid.length > 0) {
      setOverviewTab("payments");
    }
  }, [unpaid.length, activeRequests.length]);

  const currentHome = homes[homeIndex % (homes.length || 1)];
  const stepHome = (dir) => {
    if (!homes.length) return;
    setHomeIndex((i) => (i + dir + homes.length) % homes.length);
    setHomeAnimKey((k) => k + 1);
  };

  const openInvoice = (inv) => { setSelectedInvoice(inv); invoiceModal.show(); };
  const openRequest = (req) => { setSelectedRequest(req); requestModal.show(); };
  const goPay = () => navigate("/tenant/payments");
  return (
    <>
      <PagePreloader visible={!pageReady} />

      <div ref={dashboardRef} className="app-container container-fluid py-6 px-4 px-lg-8">
        <div className="mb-6 gsap-header">
                      <h1 className="text-gray-900 d6-greeting mb-1">
                        {greeting}, {greetingName}
                      </h1>
                      <p className="text-gray-500 fs-7 mb-0">
                        Your homes, rent and requests in one place.
                      </p>
                    </div>

                    {/* Row A: payment + homes + household, payments chart */}
                    <div className="row g-4 mb-5">
                      <div className="col-12 col-xl-5">
                        <div className="row g-4 h-100">
                          {/* Card 1: My Home (Full Width Brand Card) */}
                          <div className="col-12 gsap-hero">
                            <div className="d6-kpi-brand d6-kpi p-6 h-100 d-flex flex-column justify-content-between">
                              <div
                                className="d-flex align-items-start justify-content-between gap-3 position-relative"
                                style={{ zIndex: 2 }}
                              >
                                <div>
                                  <h3 className="d6-title mb-1 text-white">
                                    My home
                                  </h3>
                                  <span
                                    className="fs-8 fw-medium text-white"
                                    style={{ opacity: 0.85 }}
                                  >
                                    Units assigned to you
                                  </span>
                                </div>
                                <NavLink
                                  to="/tenant/buildings"
                                  className="d6-icon-btn on-brand"
                                  title="Open homes"
                                >
                                  <i className="ki-outline ki-arrow-up-right fs-5"></i>
                                </NavLink>
                              </div>

                              <div
                                className="position-relative my-3"
                                style={{ zIndex: 2 }}
                              >
                                <div className="d6-num text-white">
                                  {loading ? "—" : homes.length}{" "}
                                  <span
                                    className="d6-num-suffix text-white ms-1"
                                    style={{ opacity: 0.85 }}
                                  >
                                    {homes.length === 1 ? "Home" : "Homes"}
                                  </span>
                                </div>
                              </div>

                              <div
                                className="position-relative"
                                style={{ zIndex: 2 }}
                              >
                                <div className="d-flex align-items-center justify-content-between mb-2 gap-2">
                                  <span className="fs-8 fw-bold text-white">
                                    {paidPct}% of billed rent paid
                                  </span>
                                  <span
                                    className="fs-8 fw-semibold text-white"
                                    style={{ opacity: 0.85 }}
                                  >
                                    {naira(outstanding)} outstanding
                                  </span>
                                </div>
                                <div
                                  style={{
                                    height: 6,
                                    borderRadius: 999,
                                    background: "rgba(0,0,0,0.2)",
                                    overflow: "hidden",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: `${paidPct}%`,
                                      height: "100%",
                                      borderRadius: 999,
                                      background: "#ffffff",
                                      transition:
                                        "width 0.9s cubic-bezier(0.16, 1, 0.3, 1)",
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Card 2: Occupants */}
                          <div className="col-12 col-sm-6 gsap-hero">
                            <div className="d6-card d6-kpi p-6 h-100 d-flex flex-column justify-content-between">
                              <div className="d-flex align-items-start justify-content-between gap-2">
                                <div>
                                  <h3 className="text-gray-900 d6-title mb-1">
                                    Occupants
                                  </h3>
                                  <span className="text-gray-400 fs-8">
                                    Dependants & residents
                                  </span>
                                </div>
                                <NavLink
                                  to="/tenant/dependants"
                                  className="d6-icon-btn"
                                  title="Open household"
                                >
                                  <i className="ki-outline ki-arrow-up-right fs-5"></i>
                                </NavLink>
                              </div>

                              <div className="text-gray-900 d6-num my-3">
                                {loading ? "—" : dependants.length}{" "}
                                <span className="d6-num-suffix text-gray-400 ms-1">
                                  {dependants.length === 1
                                    ? "Occupant"
                                    : "Occupants"}
                                </span>
                              </div>

                              <div>
                                <div className="d-flex align-items-center justify-content-between mb-2 gap-2">
                                  <span className="fs-8 fw-bold text-gray-800">
                                    {dependants.length > 0
                                      ? `${dependants.length} in the estate`
                                      : "No occupants in the estate"}
                                  </span>
                                  <span className="fs-8 fw-semibold text-gray-500">
                                    {dependants.length > 0
                                      ? `${Math.round((codes.length / Math.max(dependants.length, 1)) * 100)}%`
                                      : "0%"}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    height: 6,
                                    borderRadius: 999,
                                    background: "#f1f1f4",
                                    overflow: "hidden",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: `${Math.min(100, Math.round((codes.length / Math.max(dependants.length, 1)) * 100))}%`,
                                      height: "100%",
                                      borderRadius: 999,
                                      background: "var(--bs-primary, #0095e8)",
                                      transition:
                                        "width 0.9s cubic-bezier(0.16, 1, 0.3, 1)",
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Card 3: Vehicles */}
                          <div className="col-12 col-sm-6 gsap-hero">
                            <div className="d6-card d6-kpi p-6 h-100 d-flex flex-column justify-content-between">
                              <div className="d-flex align-items-start justify-content-between gap-2">
                                <div>
                                  <h3 className="text-gray-900 d6-title mb-1">
                                    Vehicles
                                  </h3>
                                  <span className="text-gray-400 fs-8">
                                    Registered parking
                                  </span>
                                </div>
                                <NavLink
                                  to="/tenant/vehicles"
                                  className="d6-icon-btn"
                                  title="Open vehicles"
                                >
                                  <i className="ki-outline ki-arrow-up-right fs-5"></i>
                                </NavLink>
                              </div>

                              <div className="text-gray-900 d6-num my-3">
                                {loading ? "—" : cars.length}{" "}
                                <span className="d6-num-suffix text-gray-400 ms-1">
                                  {cars.length === 1 ? "Vehicle" : "Vehicles"}
                                </span>
                              </div>

                              <div>
                                <div className="d-flex align-items-center justify-content-between mb-2 gap-2">
                                  <span className="fs-8 fw-bold text-gray-800">
                                    {cars.length} cars in the estate
                                  </span>
                                  <span className="fs-8 fw-semibold text-gray-500">
                                    {cars.length > 0 ? "Active" : "None"}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    height: 6,
                                    borderRadius: 999,
                                    background: "#f1f1f4",
                                    overflow: "hidden",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: `${Math.min(100, (cars.length / (homes.length * 2 || 2)) * 100)}%`,
                                      height: "100%",
                                      borderRadius: 999,
                                      background: "var(--bs-primary, #0095e8)",
                                      transition:
                                        "width 0.9s cubic-bezier(0.16, 1, 0.3, 1)",
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-xl-7 gsap-card">
                        <div
                          className="card card-flush border-0 spotlight-card position-relative overflow-hidden h-100"
                          style={{ borderRadius: "16px", minHeight: "360px" }}
                        >
                          {currentHome ? (
                            <>
                              <img
                                key={homeAnimKey}
                                src={currentHome.image}
                                alt={currentHome.typeName}
                                className="spotlight-bg-img ken-burns-hero"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src =
                                    "/assets/media/zangon/3bedroomsbungalo.jpg";
                                }}
                              />
                              <div
                                className="position-absolute"
                                style={{
                                  inset: 0,
                                  background:
                                    "linear-gradient(90deg, rgba(6,40,24,0.85) 0%, rgba(6,40,24,0.45) 55%, rgba(6,40,24,0.15) 100%)",
                                  zIndex: 1,
                                }}
                              />
                              <div
                                className="position-relative p-6 p-lg-8 d-flex flex-column justify-content-between h-100"
                                style={{ zIndex: 2 }}
                              >
                                <div
                                  className="d-flex flex-column gap-2"
                                  style={{ maxWidth: 560 }}
                                >
                                  <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <span className="badge bg-white bg-opacity-25 text-white border border-white border-opacity-25 px-3 py-1.5 fs-8 fw-bold rounded-pill">
                                      {currentHome.phaseName}
                                    </span>
                                    <span className="badge bg-success bg-opacity-90 text-white px-3 py-1.5 fs-8 fw-bold rounded-pill">
                                      {currentHome.number}
                                    </span>
                                  </div>
                                  <h2 className="d6-hero-title text-white mb-1">
                                    {currentHome.typeName}
                                  </h2>
                                  <span className="text-white text-opacity-80 fs-7">
                                    Your home
                                    {currentHome.assignedAt
                                      ? ` since ${fmtDate(currentHome.assignedAt)}`
                                      : ""}{" "}
                                    · {homeIndex + 1} of {homes.length}
                                  </span>
                                </div>
                                <div className="d-flex align-items-end justify-content-between flex-wrap gap-3 pt-6">
                                  <div
                                    className="spotlight-resident-pill d-flex align-items-center"
                                    style={{ padding: "10px 18px" }}
                                  >
                                    <span className="text-white fs-7 fw-semibold">
                                      Next rent {naira(currentHome.nextAmount)}
                                      {currentHome.nextDueDate
                                        ? ` · due ${fmtDate(currentHome.nextDueDate)}`
                                        : ""}
                                    </span>
                                  </div>
                                  {homes.length > 1 && (
                                    <div className="d-flex gap-2">
                                      <button
                                        type="button"
                                        className="spotlight-nav-btn"
                                        title="Previous home"
                                        onClick={() => stepHome(-1)}
                                      >
                                        <i className="ki-outline ki-arrow-left fs-5 text-white"></i>
                                      </button>
                                      <button
                                        type="button"
                                        className="spotlight-nav-btn"
                                        title="Next home"
                                        onClick={() => stepHome(1)}
                                      >
                                        <i className="ki-outline ki-arrow-right fs-5 text-white"></i>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="d-flex flex-column align-items-center justify-content-center h-100 text-center p-8 bg-white">
                              {loading ? (
                                <span className="spinner-border text-gray-300"></span>
                              ) : (
                                <>
                                  <i className="ki-outline ki-home-2 fs-3x text-gray-300 mb-3"></i>
                                  <div className="d6-title text-gray-900 mb-1">
                                    No home assigned yet
                                  </div>
                                  <div className="fs-7 text-gray-500">
                                    Your estate manager will assign your unit.
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
{!hasPendingRentOrRequest ? (
  /* Full-Width Quick Access Pass Row (when no pending rent or requests) */
  <div className="row g-4 mb-5">
    <div className="col-12 gsap-card">
      <div className="d6-card p-5 p-xl-6">
        {/* Header with Top-Right SegTabs */}
        <CardHead
          title="Quick Access Pass"
          subtitle={
            passForm.isPermanent
              ? "Manage verified members & permanent credentials"
              : "Generate instant gate passes & security credentials"
          }
          className="mb-4"
        >
          <SegTabs
            options={[
              { value: "temporary", label: "Temporary" },
              { value: "permanent", label: "Permanent" },
            ]}
            value={passForm.isPermanent ? "permanent" : "temporary"}
            onChange={(val) =>
              setPassForm((p) => ({
                ...p,
                isPermanent: val === "permanent",
              }))
            }
          />
        </CardHead>

        {/* Quick Fill Contact Avatar Rail */}
        {dependants.length > 0 && (
          <div className="p-3 bg-light rounded-3 mb-4">
            <div className="d-flex align-items-center justify-content-between mb-2 px-1">
              <label className="d-block fs-8 fw-semibold text-gray-700 mb-1">
                Recent & Saved Contacts
              </label>
              <span className="fs-9 text-gray-500">
                {dependants.length} available
              </span>
            </div>
            <div
              className="d-flex align-items-center gap-3 overflow-x-auto pb-1"
              style={{ scrollbarWidth: "none" }}
            >
              {dependants.slice(0, 8).map((person, idx) => {
                const name =
                  person.fullName ||
                  `${person.firstName || ""} ${person.lastName || ""}`.trim() ||
                  "Guest";
                return (
                  <button
                    key={person.id || idx}
                    type="button"
                    className="d-flex flex-column align-items-center border-0 bg-transparent p-1 rounded hover-bg-white transition-all flex-shrink-0 cursor-pointer"
                    onClick={() => handleSelectSavedContact(person)}
                    title={`Quick-fill for ${name}`}
                  >
                    <Avatar src={person.imageUrl} name={name} size={40} />
                    <span
                      className="fs-9 text-gray-700 fw-medium mt-1 text-truncate"
                      style={{ maxWidth: 56 }}
                    >
                      {name.split(" ")[0]}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Compact Two-Row Form */}
        <form onSubmit={handleGeneratePass} className="d-flex flex-column gap-3">
          {/* Row 1: Name, Phone, and Photo upload side-by-side */}
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-4">
              <label className="d-block fs-8 fw-semibold text-gray-700 mb-1">
                Visitor Full Name
              </label>
              <input
                type="text"
                className="d6m-input fs-8 py-2 w-100"
                placeholder="e.g. John Doe"
                value={passForm.fullName}
                onChange={(e) =>
                  setPassForm((p) => ({
                    ...p,
                    fullName: e.target.value,
                  }))
                }
                required
              />
            </div>

            <div className="col-12 col-md-4">
              <label className="d-block fs-8 fw-semibold text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                className="d6m-input fs-8 py-2 w-100"
                placeholder="e.g. 08012345678"
                value={passForm.phoneNumber}
                onChange={(e) =>
                  setPassForm((p) => ({
                    ...p,
                    phoneNumber: e.target.value,
                  }))
                }
                required
              />
            </div>

            <div className="col-12 col-md-4">
              <label className="d-block fs-8 fw-semibold text-gray-700 mb-1">
                Visitor Photo / ID
              </label>
              {passForm.image ? (
                <div
                  className="d-flex align-items-center justify-content-between px-3 bg-light border border-gray-200 rounded-2"
                  style={{ height: 38 }}
                >
                  <div className="d-flex align-items-center gap-2 min-w-0">
                    <img
                      src={
                        typeof passForm.image === "string"
                          ? passForm.image
                          : URL.createObjectURL(passForm.image)
                      }
                      alt="Preview"
                      className="rounded-circle object-fit-cover flex-shrink-0"
                      style={{ width: 24, height: 24 }}
                    />
                    <span className="fs-8 text-gray-700 text-truncate fw-medium">
                      {passForm.image.name || "Photo attached"}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-icon btn-light-danger h-24px w-24px rounded-circle flex-shrink-0 ms-2"
                    onClick={() => setPassForm((p) => ({ ...p, image: null }))}
                    title="Remove photo"
                  >
                    <i className="ki-outline ki-cross fs-7"></i>
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="pass-image-upload-full"
                  className="d-flex align-items-center justify-content-between px-3 border border-dashed border-gray-300 rounded-2 bg-hover-light cursor-pointer transition-all text-gray-600 m-0"
                  style={{ height: 38 }}
                >
                  <span className="d-flex align-items-center gap-2 fs-8">
                    <i className="ki-outline ki-camera fs-5 text-gray-500"></i>
                    <span>Upload snapshot</span>
                  </span>
                  <span className="fs-9 text-gray-400">PNG / JPG</span>
                  <input
                    id="pass-image-upload-full"
                    type="file"
                    accept="image/*"
                    className="d-none"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setPassForm((p) => ({ ...p, image: file }));
                      }
                    }}
                  />
                </label>
              )}
            </div>
          </div>

          {/* Row 2: Category, Destination, Duration, & Submit Button */}
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-3">
              <label className="d-block fs-8 fw-semibold text-gray-700 mb-1">
                Visitor Category
              </label>
              <select
                className="d6m-input fs-8 py-2 w-100"
                value={passForm.personType}
                onChange={(e) =>
                  setPassForm((p) => ({
                    ...p,
                    personType: e.target.value,
                  }))
                }
              >
                {PERSON_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-3">
              <label className="d-block fs-8 fw-semibold text-gray-700 mb-1">
                Destination Unit
              </label>
              <select
                className="d6m-input fs-8 py-2 w-100"
                value={passForm.buildingId || homes[0]?.id || ""}
                onChange={(e) =>
                  setPassForm((p) => ({
                    ...p,
                    buildingId: e.target.value,
                  }))
                }
              >
                {homes.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.phaseName} — {h.number}
                  </option>
                ))}
              </select>
            </div>

            {/* Duration Selector (Rendered only for Temporary Pass) */}
            <div
              className={`col-12 ${
                passForm.isPermanent ? "d-none" : "col-md-3"
              }`}
            >
              <label className="d-block fs-8 fw-semibold text-gray-700 mb-1">
                Pass Duration
              </label>
              <div className="d-flex align-items-center gap-1.5 flex-nowrap">
                {DURATION_PRESETS.map((d) => (
                  <button
                    key={d.val}
                    type="button"
                    className={`btn btn-sm py-1.5 px-2.5 fs-8 rounded-pill transition-all text-nowrap flex-grow-1 ${
                      passForm.durationDays === d.val
                        ? "btn-success shadow-xs"
                        : "btn-light text-gray-700 border border-gray-200"
                    }`}
                    onClick={() =>
                      setPassForm((p) => ({
                        ...p,
                        durationDays: d.val,
                      }))
                    }
                  >
                    {d.label}
                  </button>
                ))}
                {passForm.durationDays === "custom" && (
                  <input
                    type="date"
                    min={new Date().toISOString().split("T")[0]}
                    className="d6m-input fs-8 py-1 px-2 rounded-pill"
                    style={{ maxWidth: 130 }}
                    value={passForm.customDate}
                    onChange={(e) =>
                      setPassForm((p) => ({
                        ...p,
                        customDate: e.target.value,
                      }))
                    }
                  />
                )}
              </div>
            </div>

            {/* Action Button */}
            <div
              className={`col-12 ${
                passForm.isPermanent ? "col-md-6" : "col-md-3"
              }`}
            >
              <button
                type="submit"
                className="btn btn-success d6-mbtn w-100 py-2 fs-8 fw-bold d-flex flex-center"
                style={{ minHeight: 38 }}
                disabled={passLoading}
              >
                {passLoading ? (
                  <span className="spinner-border spinner-border-sm me-2"></span>
                ) : (
                  <i className="ki-outline ki-security-check fs-5 me-2.5 text-white"></i>
                )}
                {passForm.isPermanent
                  ? "Register Permanent Dependant"
                  : "Generate Pass"}
              </button>
            </div>
          </div>

          {passError && <div className="d6m-error mt-1">{passError}</div>}
        </form>
      </div>
    </div>
  </div>
) : (
  /* Quick Access Pass (col-6) & Overview (col-6) Row when pending rent or requests exist */
  <div className="row g-4 mb-5">
    {/* Left: Quick Access Pass Card */}
    <div className="col-12 col-xl-6 gsap-card">
      <div className="d6-card p-6 h-100 d-flex flex-column">
        <CardHead
          title="Quick Access Pass"
          subtitle={
            passForm.isPermanent
              ? "Manage verified members & permanent credentials"
              : "Generate instant gate passes & security credentials"
          }
          className="mb-4"
        >
          <SegTabs
            options={[
              { value: "temporary", label: "Temporary" },
              { value: "permanent", label: "Permanent" },
            ]}
            value={passForm.isPermanent ? "permanent" : "temporary"}
            onChange={(val) =>
              setPassForm((p) => ({
                ...p,
                isPermanent: val === "permanent",
              }))
            }
          />
        </CardHead>

        {/* Quick Fill Contact Avatar Rail */}
        {dependants.length > 0 && (
          <div className="p-3 bg-light rounded-3 mb-4">
            <div className="d-flex align-items-center justify-content-between mb-2 px-1">
              <label className="d-block fs-8 fw-semibold text-gray-700 mb-1">
                Recent & Saved Contacts
              </label>
              <span className="fs-9 text-gray-500">
                {dependants.length} available
              </span>
            </div>
            <div
              className="d-flex align-items-center gap-3 overflow-x-auto pb-1"
              style={{ scrollbarWidth: "none" }}
            >
              {dependants.slice(0, 8).map((person, idx) => {
                const name =
                  person.fullName ||
                  `${person.firstName || ""} ${person.lastName || ""}`.trim() ||
                  "Guest";
                return (
                  <button
                    key={person.id || idx}
                    type="button"
                    className="d-flex flex-column align-items-center border-0 bg-transparent p-1 rounded hover-bg-white transition-all flex-shrink-0 cursor-pointer"
                    onClick={() => handleSelectSavedContact(person)}
                    title={`Quick-fill for ${name}`}
                  >
                    <Avatar src={person.imageUrl} name={name} size={40} />
                    <span
                      className="fs-9 text-gray-700 fw-medium mt-1 text-truncate"
                      style={{ maxWidth: 56 }}
                    >
                      {name.split(" ")[0]}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Pass Form */}
        <form
          onSubmit={handleGeneratePass}
          className="d-flex flex-column gap-3 flex-grow-1"
        >
          {/* 1. Primary Contact Information (First) */}
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="d-block fs-8 fw-semibold text-gray-700 mb-1">
                Visitor Full Name
              </label>
              <input
                type="text"
                className="d6m-input fs-8 py-2 w-100"
                placeholder="e.g. John Doe"
                value={passForm.fullName}
                onChange={(e) =>
                  setPassForm((p) => ({
                    ...p,
                    fullName: e.target.value,
                  }))
                }
                required
              />
            </div>
            <div className="col-12 col-md-6">
              <label className="d-block fs-8 fw-semibold text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                className="d6m-input fs-8 py-2 w-100"
                placeholder="e.g. 08012345678"
                value={passForm.phoneNumber}
                onChange={(e) =>
                  setPassForm((p) => ({
                    ...p,
                    phoneNumber: e.target.value,
                  }))
                }
                required
              />
            </div>
          </div>

          {/* 2. Photo / Image Upload Field */}
          <div>
            <label className="d-block fs-8 fw-semibold text-gray-700 mb-1">
              Visitor Photo / ID Snapshot
            </label>
            {passForm.image ? (
              <div className="d-flex align-items-center justify-content-between p-2.5 border border-gray-200 rounded-3 bg-light">
                <div className="d-flex align-items-center gap-3 min-w-0">
                  <img
                    src={
                      typeof passForm.image === "string"
                        ? passForm.image
                        : URL.createObjectURL(passForm.image)
                    }
                    alt="Preview"
                    className="rounded-circle object-fit-cover flex-shrink-0"
                    style={{ width: 42, height: 42 }}
                  />
                  <div className="min-w-0">
                    <div className="fs-8 fw-semibold text-gray-800 text-truncate">
                      {passForm.image.name || "visitor-photo.jpg"}
                    </div>
                    <div className="fs-9 text-success fw-medium">Ready for upload</div>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-light-danger h-30px w-30px rounded-circle"
                  onClick={() => setPassForm((p) => ({ ...p, image: null }))}
                  title="Remove photo"
                >
                  <i className="ki-outline ki-cross fs-6"></i>
                </button>
              </div>
            ) : (
              <label
                htmlFor="pass-image-upload"
                className="d-flex flex-column align-items-center justify-content-center p-3 border border-dashed border-gray-300 rounded-3 bg-hover-light cursor-pointer transition-all"
                style={{ minHeight: 74 }}
              >
                <div className="d-flex align-items-center gap-2 text-gray-600">
                  <i className="ki-outline ki-camera fs-4 text-gray-500"></i>
                  <span className="fs-8 fw-semibold">Click to upload photo or capture ID</span>
                </div>
                <span className="fs-9 text-gray-400 mt-0.5">PNG, JPG, or WEBP up to 5MB</span>
                <input
                  id="pass-image-upload"
                  type="file"
                  accept="image/*"
                  className="d-none"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setPassForm((p) => ({ ...p, image: file }));
                    }
                  }}
                />
              </label>
            )}
          </div>

          {/* 3. Visitor Classification & Destination Unit */}
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="d-block fs-8 fw-semibold text-gray-700 mb-1">
                Visitor Category
              </label>
              <select
                className="d6m-input fs-8 py-2 w-100"
                value={passForm.personType}
                onChange={(e) =>
                  setPassForm((p) => ({
                    ...p,
                    personType: e.target.value,
                  }))
                }
              >
                {PERSON_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-6">
              <label className="d-block fs-8 fw-semibold text-gray-700 mb-1">
                Destination Unit
              </label>
              <select
                className="d6m-input fs-8 py-2 w-100"
                value={passForm.buildingId || homes[0]?.id || ""}
                onChange={(e) =>
                  setPassForm((p) => ({
                    ...p,
                    buildingId: e.target.value,
                  }))
                }
              >
                {homes.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.phaseName} — {h.number}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* 4. Duration Configuration (Temporary Only) */}
          {!passForm.isPermanent && (
            <div>
              <label className="d-block fs-8 fw-semibold text-gray-700 mb-1">
                Pass Duration
              </label>
              <div className="d-flex flex-wrap align-items-center gap-2">
                {DURATION_PRESETS.map((d) => (
                  <button
                    key={d.val}
                    type="button"
                    className={`btn btn-sm py-1.5 px-3 fs-8 rounded-pill transition-all ${
                      passForm.durationDays === d.val
                        ? "btn-success shadow-xs"
                        : "btn-light text-gray-700 border border-gray-200"
                    }`}
                    onClick={() =>
                      setPassForm((p) => ({
                        ...p,
                        durationDays: d.val,
                      }))
                    }
                  >
                    {d.label}
                  </button>
                ))}
                {passForm.durationDays === "custom" && (
                  <input
                    type="date"
                    min={new Date().toISOString().split("T")[0]}
                    className="d6m-input fs-8 py-1 px-3 rounded-pill ms-auto"
                    style={{ maxWidth: 170 }}
                    value={passForm.customDate}
                    onChange={(e) =>
                      setPassForm((p) => ({
                        ...p,
                        customDate: e.target.value,
                      }))
                    }
                  />
                )}
              </div>
            </div>
          )}

          {passError && <div className="d6m-error mt-1">{passError}</div>}

          {/* 5. Submit */}
          <div className="pt-2 mt-auto">
            <button
              type="submit"
              className="btn btn-success d6-mbtn w-100 py-3"
              disabled={passLoading}
            >
              {passLoading ? (
                <span className="spinner-border spinner-border-sm me-2"></span>
              ) : (
                <i className="ki-outline ki-security-check fs-4 me-2 text-white"></i>
              )}
              {passForm.isPermanent
                ? "Register Permanent Dependant"
                : "Generate Instant Gate Pass"}
            </button>
          </div>
        </form>
      </div>
    </div>

    {/* Right: Overview Card (Payments & Requests Tabs) */}
    <div className="col-12 col-xl-6 gsap-card">
      <div className="d6-card p-6 h-100 d-flex flex-column">
        <CardHead
          title="Overview"
          subtitle={
            overviewTab === "payments"
              ? "Upcoming invoices pending settlement"
              : "Active maintenance and service tickets"
          }
          className="mb-3"
        >
          <SegTabs
            options={[
              { value: "payments", label: "Payments" },
              { value: "requests", label: "Requests" },
            ]}
            value={overviewTab}
            onChange={setOverviewTab}
          />
          {overviewTab === "payments" ? (
            <button
              type="button"
              className="btn btn-light-success d-flex flex-center h-30px rounded-pill px-3 fs-9 fw-bold"
              onClick={goPay}
            >
              <i className="ki-outline ki-wallet fs-7 me-2"></i>
              Pay Now
            </button>
          ) : (
            <div className="d-flex align-items-center gap-1">
              <button
                type="button"
                className="btn btn-success d-flex flex-center h-30px rounded-pill px-3 fs-9 fw-bold"
                onClick={() => reportModal.show()}
              >
                <i className="ki-outline ki-plus fs-7 me-2 text-white"></i>
                Report
              </button>
              <NavLink
                to="/tenant/maintenance"
                className="d6-icon-btn h-30px w-30px"
                title="All requests"
              >
                <i className="ki-outline ki-arrow-up-right fs-6"></i>
              </NavLink>
            </div>
          )}
        </CardHead>

        {/* Tab 1: Payments */}
        {overviewTab === "payments" && (
          <div className="d-flex flex-column mh-420px h-420px overflow-y-auto pe-1">
            {loading ? (
              [1, 2, 3, 4].map((n) => (
                <div
                  key={n}
                  className="d-flex align-items-center mb-3 p-3.5 d6-list-card border border-gray-100"
                >
                  <Shimmer w={40} h={40} r={999} />
                  <div className="flex-grow-1 d-flex flex-column gap-2 ms-3">
                    <Shimmer w="45%" h={12} />
                    <Shimmer w="30%" h={10} />
                  </div>
                  <Shimmer w={75} h={20} r={8} />
                </div>
              ))
            ) : unpaid.length === 0 ? (
              <div className="text-center py-12 my-auto">
                <i className="ki-outline ki-check-circle fs-3x text-success mb-3 d-block"></i>
                <div className="d6-title text-gray-900 mb-1">
                  You're all paid up
                </div>
                <div className="fs-7 text-gray-500">
                  No pending invoices at the moment.
                </div>
              </div>
            ) : (
              unpaid.map((inv) => {
                const meta = getDueMeta(
                  today,
                  String(inv.dueDate).split("T")[0],
                );
                return (
                  <div
                    key={inv.id}
                    className="d-flex align-items-center mb-3 py-6 px-6 d6-list-card border border-hover-success cursor-pointer transition-all"
                    style={{ minHeight: 90 }}
                    onClick={() => openInvoice(inv)}
                  >
                    <div className="symbol symbol-45px symbol-circle me-5 flex-shrink-0">
                      <img
                        src={getHouseImage(inv.home)}
                        alt=""
                        style={{ objectFit: "cover" }}
                      />
                    </div>
                    <div className="d-flex flex-column flex-grow-1 min-w-0">
                      <span className="fs-6 fw-bold text-gray-900 text-truncate">
                        {inv.home}
                      </span>
                      <div className="text-gray-500 fs-7 text-truncate">
                        {inv.period || "Rent invoice"}
                      </div>
                    </div>
                    <div className="d-flex gap-8 text-end ms-6 flex-shrink-0">
                      <div>
                        <div className="text-gray-500 fs-8">
                          Amount due
                        </div>
                        <div className="fw-semibold text-gray-900">
                          {naira(inv.balance)}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 fs-8">
                          Due date
                        </div>
                        <div
                          className={`fw-semibold ${meta.expiryClass}`}
                        >
                          {fmtDate(inv.dueDate)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Tab 2: Requests */}
        {overviewTab === "requests" && (
          <div className="d-flex flex-column mh-420px h-420px overflow-y-auto pe-1">
            {loading ? (
              [1, 2, 3].map((n) => (
                <div
                  key={n}
                  className="d-flex align-items-center mb-3 p-3.5 d6-list-card border border-gray-100"
                >
                  <Shimmer w={40} h={40} r={999} />
                  <div className="flex-grow-1 d-flex flex-column gap-2 ms-3">
                    <Shimmer w="50%" h={12} />
                    <Shimmer w="30%" h={10} />
                  </div>
                </div>
              ))
            ) : listedRequests.length === 0 ? (
              <div className="text-center py-12 my-auto">
                <i className="ki-outline ki-element-plus fs-3x text-gray-400 mb-3 d-block"></i>
                <div className="d6-title text-gray-900 mb-1">
                  No active requests
                </div>
                <div className="fs-7 text-gray-500">
                  Report an issue and we'll track it here.
                </div>
              </div>
            ) : (
              listedRequests.map((r) => (
                <div
                  key={r.id}
                  className="d-flex align-items-center mb-3 py-6 px-6 d6-list-card border border-gray-200 border-hover-success cursor-pointer transition-all position-relative overflow-hidden"
                  style={{ minHeight: 90 }}
                  onClick={() => openRequest(r)}
                >
                  <div
                    className={`position-absolute top-0 start-0 h-100 w-4px ${
                      REQUEST_BAR[r.status] || "bg-warning"
                    }`}
                  />
                  <div className="symbol symbol-45px symbol-circle me-5 ms-2 flex-shrink-0">
                    <span className="symbol-label bg-light text-gray-600">
                      <i className="ki-outline ki-wrench fs-3 text-gray-600"></i>
                    </span>
                  </div>
                  <div className="d-flex flex-column flex-grow-1 overflow-hidden">
                    <span className="fs-6 fw-bold text-gray-900 text-truncate">
                      {r.title}
                    </span>
                    <div className="text-gray-500 fs-7 text-truncate">
                      {r.home || r.category} · {r.category}
                    </div>
                  </div>
                  <div className="text-end ms-6 flex-shrink-0">
                    <div className="text-gray-500 fs-8">
                      Status
                    </div>
                    <div
                      className={`fw-semibold ${REQUEST_TEXT[r.status] || "text-warning"}`}
                    >
                      {r.status}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  </div>
)}

      </div>

        {/* Access Pass Modal */}
        <ModalShell
          modalRef={accessPassModalRef}
          icon="ki-security-user"
          title="Access Pass Generated"
          width={440}
          onClose={() => accessPassModal.hide()}
          footer={
            <div className="d-flex align-items-center justify-content-between w-100 gap-2">
              <button
                type="button"
                className="btn btn-light d6-mbtn"
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: "Estate Access Pass",
                      text: `Gate Pass for ${activePassData?.fullName}: Code is ${activePassData?.code}`,
                    });
                  } else {
                    navigator.clipboard.writeText(activePassData?.code || "");
                    toast.success("Gate code copied to clipboard!");
                  }
                }}
              >
                <i className="ki-outline ki-copy me-1"></i>Share Pass
              </button>
              <button
                type="button"
                className="btn btn-success d6-mbtn"
                onClick={() => accessPassModal.hide()}
              >
                Done
              </button>
            </div>
          }
        >
          {activePassData && (
            <div className="d-flex flex-column align-items-center text-center py-2">
              <span className="badge bg-light-success text-success fs-8 fw-bold rounded-pill px-3 py-1 mb-3">
                {activePassData.isPermanent
                  ? "Permanent Credential"
                  : "Active Gate Pass"}
              </span>

              <div className="bg-light p-4 rounded-3 border border-gray-200 mb-3">
                <img
                  src={activePassData.qrCodeFileUrl || `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
                    activePassData.qrCodeData || activePassData.code || "",
                  )}`}
                  alt="Access QR Code"
                  className="rounded-2"
                  style={{ width: 140, height: 140 }}
                />
              </div>

              <div className="text-gray-400 fs-9 text-uppercase tracking-widest fw-bold">
                Numeric Entry Code
              </div>
              <div
                className="d6-num text-success my-1 letter-spacing-2"
                style={{ letterSpacing: "3px" }}
              >
                {activePassData.code}
              </div>

              <div className="d6m-kv w-100 mt-4 text-start">
                <div className="d6m-kv-cell">
                  <span className="d6m-k">Guest</span>
                  <span className="d6m-v text-truncate d-block">
                    {activePassData.fullName}
                  </span>
                </div>
                <div className="d6m-kv-cell">
                  <span className="d6m-k">Type</span>
                  <span className="d6m-v">{activePassData.personType}</span>
                </div>
                <div className="d6m-kv-cell">
                  <span className="d6m-k">Destination</span>
                  <span className="d6m-v">{activePassData.buildingName}</span>
                </div>
                <div className="d6m-kv-cell">
                  <span className="d6m-k">Validity</span>
                  <span className="d6m-v">
                    {activePassData.isPermanent
                      ? "Unlimited"
                      : `${activePassData.validDays || 1} day(s)`}
                  </span>
                </div>
              </div>
            </div>
          )}
        </ModalShell>
        <InvoiceModal
          modalRef={invoiceModalRef}
          invoice={selectedInvoice}
          today={today}
          onClose={() => setSelectedInvoice(null)}
          onPay={goPay}
        />
        <RequestModal
          modalRef={requestModalRef}
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
        />
        <ReportIssueModal
          modalRef={reportModalRef}
          homes={homes}
          categories={categories}
          onSubmitted={(req) =>
            setData((d) => ({ ...d, requests: [req, ...d.requests] }))
          }
        />
      {searchOpen && (
        <GlobalSearchModal
          activeQuery={activeSearchQuery}
          category={searchCategory}
          setCategory={setSearchCategory}
          results={globalSearchResults}
          onClose={() => setSearchOpen(false)}
          onSelect={(item) => {
            setSearchOpen(false);
            setGlobalSearch("");
            setActiveSearchQuery("");
            navigate(item.route);
          }}
        />
      )}
    </>
  );
}
